/**
 * The catalogue's names in the content language NOT on screen — the second half of a search that
 * matches Polish and English at once, as the phone's server-side `ILIKE` over both does.
 *
 * ── A second read of the same query, never a new one ─────────────────────────────────────────
 * `exerciseLibraryQueryOptions(lang)` and `exercisesByIdsQueryOptions(ids, lang)` already key by
 * language, so the other language is simply the other cache entry: a trainer who flips the content
 * language finds it warm, and `exerciseLibraryRoot` invalidates both at once.
 *
 * ── It never holds the screen ─────────────────────────────────────────────────────────────────
 * No pending, no error: while the read is open or after it failed the map is EMPTY and the search
 * answers in the displayed language alone. Nothing is drawn from this read, so there is nothing to
 * reserve and nothing to announce — a failed second read costs a trainer the cross-language match
 * and nothing else.
 *
 * `select` projects the payload to the map, and TanStack keeps its answer while the data and the
 * function stay the same — which is why `namesById` is a module-level function, not a lambda.
 */

import { useQuery } from '@tanstack/react-query';

import type { TrainWithMeModulesExercisesDtosResponsesGetExerciseResponse as ApiExercise } from '~/shared/api/generated/model';
import { otherContentLanguage, useContentLanguage } from '~/shared/i18n/contentLanguage';

import { exerciseLibraryQueryOptions, exercisesByIdsQueryOptions } from '../api/exerciseQueries';
import type { OtherLanguageNames } from '../model/exerciseCatalogue';
import { toExerciseListItems } from '../model/exerciseListItem';

const NOTHING_YET: OtherLanguageNames = new Map();

/**
 * The names, and whether their read FAILED — the one thing a host says about this read. A failed
 * read leaves the search in the displayed language alone, so a „nothing matched" may be wrong about
 * a term typed in the other language, and the empty state says it could not check.
 */
export interface OtherLanguageNamesState {
  readonly names: OtherLanguageNames;
  readonly failed: boolean;
}

function namesById(data: readonly ApiExercise[]): OtherLanguageNames {
  return new Map(toExerciseListItems(data).map((item) => [item.id, item.name]));
}

/**
 * The whole catalogue's names in the other language.
 *
 * `enabled` is whether the host's library is actually ON SCREEN, not merely mounted: the builder
 * keeps its drawer mounted while it is put away, and a closed drawer must not fetch a second
 * catalogue nobody is searching. Once read, the answer stays cached when the drawer closes again.
 */
export function useOtherLanguageNames(enabled = true): OtherLanguageNamesState {
  const other = otherContentLanguage(useContentLanguage());
  const read = useQuery({ ...exerciseLibraryQueryOptions(other), select: namesById, enabled });
  return { names: read.data ?? NOTHING_YET, failed: read.isError };
}

/** The same, for a handful of movements the host already reads by id — the lean batch. */
export function useOtherLanguageNamesOf(ids: readonly number[]): OtherLanguageNamesState {
  const other = otherContentLanguage(useContentLanguage());
  const read = useQuery({ ...exercisesByIdsQueryOptions(ids, other), select: namesById });
  return { names: read.data ?? NOTHING_YET, failed: read.isError };
}
